/* iter55z-d — Admin sidebar card: LLM Cost & Balance.
 *
 * Aggregates recent LLM COGS (Anthropic + OpenAI + Google via the
 * Emergent Universal Key) alongside the corresponding user-paid
 * revenue. Emergent does not expose a live "balance" endpoint, so
 * we show spend metrics and route the admin to the Emergent Profile
 * for the actual key balance / top-up.
 *
 * iter55z-e improvement — daily spend-alert threshold. When today's
 * COGS crosses the configured `spend_alert_usd`, the card flashes red
 * and a toast fires once per session. Threshold is editable inline.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { llmCostApi, modelsApi } from '@/lib/api';
import { toast } from 'sonner';
import { AlertTriangle, Bell, Coins, ExternalLink, Loader2, RefreshCcw, Check, X, TrendingDown } from 'lucide-react';

const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const BUCKETS = [
  { key: 'today', label: 'Today' },
  { key: 'week',  label: '7 days' },
  { key: 'month', label: '30 days' },
];

export default function LlmCostCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [editingAlert, setEditingAlert] = useState(false);
  const [alertDraft, setAlertDraft] = useState('');
  const [models, setModels] = useState([]);
  const [editingDg, setEditingDg] = useState(false);
  const [dgDraft, setDgDraft] = useState({ enabled: false, threshold: '', fallback: '' });
  const alertedRef = useRef(false);
  const downgradeToastedRef = useRef(false);

  const load = async () => {
    setBusy(true);
    try {
      const d = await llmCostApi.summary();
      setData(d);
      // One-shot toast if the daily budget is now exceeded
      if (d?.alert_active && !alertedRef.current) {
        alertedRef.current = true;
        toast.warning(
          `LLM spend today ($${(d.buckets?.today?.cogs_usd || 0).toFixed(2)}) exceeded your alert of $${(d.spend_alert_usd || 0).toFixed(2)}.`
        );
      }
      // One-shot toast when the downgrade brake engages
      if (d?.downgrade_active && !downgradeToastedRef.current) {
        downgradeToastedRef.current = true;
        toast.info(
          `Auto-downgrade engaged — new generations routed to ${d.model_downgrade?.fallback_model_id || 'fallback model'}.`
        );
      }
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setHidden(true);
      } else {
        toast.error('Failed to load LLM cost summary.');
      }
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Load the model catalog for the fallback picker (once).
    (async () => {
      try {
        const m = await modelsApi.list();
        setModels(Array.isArray(m?.models) ? m.models : Array.isArray(m) ? m : []);
      } catch {
        /* non-fatal — dropdown falls back to a plain input */
      }
    })();
  }, []);

  const saveAlert = async () => {
    const v = Number(alertDraft);
    if (Number.isNaN(v) || v < 0) {
      toast.error('Enter a non-negative dollar amount.');
      return;
    }
    setBusy(true);
    try {
      await llmCostApi.setSpendAlert(v);
      toast.success(v > 0 ? `Alert set at $${v.toFixed(2)}/day` : 'Alert disabled');
      alertedRef.current = false;
      setEditingAlert(false);
      await load();
    } catch {
      toast.error('Failed to save alert.');
    } finally {
      setBusy(false);
    }
  };

  const startEditDg = () => {
    const md = data?.model_downgrade || {};
    setDgDraft({
      enabled: !!md.enabled,
      threshold: md.daily_threshold_usd > 0 ? String(md.daily_threshold_usd) : '',
      fallback: md.fallback_model_id || 'gemini-3-flash',
    });
    setEditingDg(true);
  };

  const saveDg = async () => {
    const t = Number(dgDraft.threshold);
    if (Number.isNaN(t) || t < 0) {
      toast.error('Enter a non-negative threshold.');
      return;
    }
    if (!dgDraft.fallback) {
      toast.error('Pick a fallback model.');
      return;
    }
    setBusy(true);
    try {
      await llmCostApi.setDowngrade(dgDraft.enabled, t, dgDraft.fallback);
      toast.success(
        dgDraft.enabled
          ? `Auto-downgrade ON — fallback ${dgDraft.fallback} at $${t.toFixed(2)}/day`
          : 'Auto-downgrade OFF'
      );
      downgradeToastedRef.current = false;
      setEditingDg(false);
      await load();
    } catch {
      toast.error('Failed to save auto-downgrade config.');
    } finally {
      setBusy(false);
    }
  };

  if (hidden) return null;
  if (!data) {
    return (
      <div
        className="rounded-md border border-border bg-muted/40 p-2"
        data-testid="admin-llm-cost-card-loading"
      >
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading LLM cost…
        </div>
      </div>
    );
  }

  const buckets  = data.buckets || {};
  const topKinds = data.top_kinds || [];
  const alertActive = !!data.alert_active;
  const alertUsd    = Number(data.spend_alert_usd || 0);
  const md          = data.model_downgrade || {};
  const dgActive    = !!data.downgrade_active;

  const shellCls = alertActive
    ? 'rounded-md border border-red-400 bg-red-50 p-2 shadow-panel animate-pulse-slow'
    : 'rounded-md border border-emerald-300/60 bg-emerald-50/70 p-2 shadow-panel';
  const accent = alertActive ? 'text-red-900' : 'text-emerald-900';
  const accentMuted = alertActive ? 'text-red-800/70' : 'text-emerald-800/70';

  return (
    <div className={shellCls} data-testid="admin-llm-cost-card">
      <div className={`mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${accent}`}>
        {alertActive
          ? <AlertTriangle className="h-3.5 w-3.5" data-testid="admin-llm-cost-alert-icon" />
          : <Coins className="h-3.5 w-3.5" />
        }
        LLM Cost &amp; Balance
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className={`ml-auto inline-flex h-4 w-4 items-center justify-center rounded-sm ${accent} hover:bg-white/60 disabled:opacity-50`}
          title="Refresh"
          data-testid="admin-llm-cost-refresh-btn"
        >
          {busy
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <RefreshCcw className="h-3 w-3" />
          }
        </button>
      </div>

      {alertActive && (
        <div
          className="mb-1.5 rounded-sm bg-red-200/80 px-1.5 py-1 text-[10.5px] font-semibold text-red-900"
          data-testid="admin-llm-cost-alert-banner"
        >
          Daily spend exceeds ${alertUsd.toFixed(2)} threshold
        </div>
      )}

      {/* Bucket totals */}
      <div className="grid grid-cols-3 gap-1" data-testid="admin-llm-cost-buckets">
        {BUCKETS.map(({ key, label }) => {
          const b = buckets[key] || {};
          const cogs   = b.cogs_usd || 0;
          const rev    = b.revenue_usd || 0;
          const margin = rev - cogs;
          return (
            <div
              key={key}
              className={`rounded-sm bg-white/80 px-1.5 py-1 ${accent}`}
              data-testid={`admin-llm-cost-bucket-${key}`}
            >
              <div className={`text-[9px] uppercase tracking-wider ${accentMuted}`}>{label}</div>
              <div
                className="text-[11px] font-semibold leading-tight"
                data-testid={`admin-llm-cost-cogs-${key}`}
              >
                {fmtUsd(cogs)}
              </div>
              <div className={`text-[9px] leading-tight ${accentMuted}`}>
                rev {fmtUsd(rev)}
                <span className={margin >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                  {' · '}{margin >= 0 ? '+' : ''}{fmtUsd(margin)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Top-3 kinds (last 30 days) */}
      {topKinds.length > 0 && (
        <div className="mt-2" data-testid="admin-llm-cost-top-kinds">
          <div className={`mb-0.5 text-[9px] uppercase tracking-wider ${accentMuted}`}>
            Top spend (30d)
          </div>
          <ul className="space-y-0.5">
            {topKinds.map((k) => (
              <li
                key={k.kind}
                className={`flex items-center justify-between text-[10.5px] ${accent}`}
                data-testid={`admin-llm-cost-kind-${k.kind}`}
              >
                <span className="truncate">{k.kind}</span>
                <span className="ml-1 font-semibold tabular-nums">{fmtUsd(k.cogs_usd)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {topKinds.length === 0 && (
        <div
          className={`mt-2 text-[10px] italic ${accentMuted}`}
          data-testid="admin-llm-cost-empty"
        >
          No LLM spend recorded yet.
        </div>
      )}

      {/* Spend-alert threshold row */}
      <div className={`mt-2 border-t ${alertActive ? 'border-red-300' : 'border-emerald-300/60'} pt-1.5`}>
        {editingAlert ? (
          <div className="flex items-center gap-1" data-testid="admin-llm-cost-alert-edit">
            <span className={`text-[10.5px] ${accent}`}>$</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={alertDraft}
              onChange={(e) => setAlertDraft(e.target.value)}
              className="h-6 flex-1 text-[10.5px]"
              data-testid="admin-llm-cost-alert-input"
              autoFocus
            />
            <span className={`text-[10.5px] ${accentMuted}`}>/day</span>
            <button
              type="button"
              onClick={saveAlert}
              disabled={busy}
              className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              data-testid="admin-llm-cost-alert-save"
              title="Save"
            >
              <Check className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => setEditingAlert(false)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-white text-muted-foreground hover:bg-muted"
              data-testid="admin-llm-cost-alert-cancel"
              title="Cancel"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setAlertDraft(alertUsd > 0 ? String(alertUsd) : ''); setEditingAlert(true); }}
            className={`inline-flex w-full items-center gap-1 rounded-sm px-1 py-0.5 text-[10.5px] hover:bg-white/60 ${accent}`}
            data-testid="admin-llm-cost-alert-toggle"
          >
            <Bell className="h-3 w-3" />
            {alertUsd > 0
              ? <>Alert at <b className="tabular-nums">{fmtUsd(alertUsd)}</b>/day</>
              : <>Set daily spend alert…</>
            }
          </button>
        )}
      </div>

      {/* Auto-downgrade cost brake */}
      <div className={`mt-1.5 border-t ${alertActive ? 'border-red-300' : 'border-emerald-300/60'} pt-1.5`}>
        {editingDg ? (
          <div className="space-y-1" data-testid="admin-llm-cost-downgrade-edit">
            <label className={`flex items-center gap-1 text-[10.5px] ${accent}`}>
              <input
                type="checkbox"
                checked={dgDraft.enabled}
                onChange={(e) => setDgDraft({ ...dgDraft, enabled: e.target.checked })}
                data-testid="admin-llm-cost-downgrade-enabled"
              />
              Enable auto-downgrade
            </label>
            <div className="flex items-center gap-1">
              <span className={`text-[10.5px] ${accent}`}>$</span>
              <Input
                type="number" step="0.01" min="0"
                value={dgDraft.threshold}
                onChange={(e) => setDgDraft({ ...dgDraft, threshold: e.target.value })}
                className="h-6 w-14 text-[10.5px]"
                placeholder="threshold"
                data-testid="admin-llm-cost-downgrade-threshold"
              />
              <span className={`text-[10.5px] ${accentMuted}`}>/day →</span>
              <select
                value={dgDraft.fallback}
                onChange={(e) => setDgDraft({ ...dgDraft, fallback: e.target.value })}
                className="h-6 flex-1 rounded-sm border border-input bg-background px-1 text-[10.5px]"
                data-testid="admin-llm-cost-downgrade-fallback"
              >
                {models.length === 0 && (
                  <option value="gemini-3-flash">gemini-3-flash</option>
                )}
                {models.map((m) => (
                  <option key={m.id || m.model_id} value={m.id || m.model_id}>
                    {m.label || m.id || m.model_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={saveDg}
                disabled={busy}
                className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                data-testid="admin-llm-cost-downgrade-save"
                title="Save"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => setEditingDg(false)}
                className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-white text-muted-foreground hover:bg-muted"
                data-testid="admin-llm-cost-downgrade-cancel"
                title="Cancel"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditDg}
            className={`inline-flex w-full items-center gap-1 rounded-sm px-1 py-0.5 text-[10.5px] hover:bg-white/60 ${accent}`}
            data-testid="admin-llm-cost-downgrade-toggle"
          >
            <TrendingDown className="h-3 w-3" />
            {md.enabled ? (
              dgActive ? (
                <span data-testid="admin-llm-cost-downgrade-active">
                  <b>Downgrade ON</b> — routing to <b>{md.fallback_model_id}</b>
                </span>
              ) : (
                <>Auto-downgrade armed at <b className="tabular-nums">{fmtUsd(md.daily_threshold_usd)}</b></>
              )
            ) : (
              <>Enable auto-downgrade brake…</>
            )}
          </button>
        )}
      </div>

      {/* Manage Universal Key CTA */}
      <div className={`mt-1.5 border-t ${alertActive ? 'border-red-300' : 'border-emerald-300/60'} pt-1.5`}>
        <Button
          asChild
          size="sm"
          variant="outline"
          className={`h-6 w-full justify-center gap-1 border-emerald-400 bg-white text-[10.5px] ${accent} hover:bg-emerald-100`}
          data-testid="admin-llm-cost-manage-btn"
        >
          <a
            href="https://app.emergent.sh/profile"
            target="_blank"
            rel="noopener noreferrer"
          >
            Manage Universal Key
            <ExternalLink className="h-3 w-3" />
          </a>
        </Button>
      </div>
    </div>
  );
}
